import { message } from 'antd';
import { axiosApi } from '../axios-api';
import { addLocalStorage, handleOneLineError } from '../helpers';

export async function fetchInvitationOrganizationByCode(code) {
  try {
    const { data } = await axiosApi.get(
      `/v2/accounts/registration/organization/?code=${code}`,
    );
    if (Object.keys(data).length === 0) return;

    return data;
  } catch (error) {
    if (error.isAxiosError) {
      message.warning({
        content: error.response.data.detail,
        duration: 3,
      });
    } else {
      throw new Error(error);
    }
  }
}

export async function completeInvitedRegistration(requestBody, setError, history) {
  try {
    const { data } = await axiosApi.post('/v2/accounts/registration/invitation/', requestBody);
    if (!data) return;
    message.success({
      content: 'Account has been created successfully, please log in',
      duration: 3,
    });
    setError({});
    history.push('/login');
  } catch (error) {
    if (error.isAxiosError) {
      const { data } = error.response;
      const errorFields = {};

      if (!data.username && !data.invitation_code && !data.password) {
        message.warning({
          content: 'Registration finished with error! Try again in a while.',
          duration: 3,
        });
        return;
      }

      if (data.invitation_code) {
        message.warning({
          content: data.invitation_code[0],
          duration: 3,
        });
      }

      if (data.username) {
        errorFields.username = data.username[0];
      }

      if (data.password) {
        errorFields.password =
          'Password has to be at least 8 characters long and includes both letters and numbers';
      }

      setError(errorFields);
    } else {
      throw new Error(error);
    }
  }
}

export async function confirmRegistrationWithCode(code, setLoading) {
  try {
    setLoading(true);
    const { data } = await axiosApi.patch(
      `/v2/accounts/registration-confirmation/?code=${code}`,
    );
    return data;
  } catch (error) {
    if (error.isAxiosError) {
      message.warning({
        content: 'User not found or the confirmation link is expired',
        duration: 5,
      });
    } else {
      throw new Error(error);
    }
  } finally {
    setLoading(false);
  }
}

export async function postLoginRequest(requestData) {
  const { data } = await axiosApi.post('/v2/accounts/login/', requestData);
  return data;
}

export async function postLogoutRequest() {
  const { status } = await axiosApi.post('/v2/accounts/logout/');
  return status;
}

export async function postRegisterRequest(requestBody) {
  const { data } = await axiosApi.post('v2/accounts/registration/', requestBody);
  return data;
}

export async function requestPasswordReset(email, setError, setLoading) {
  try {
    setLoading(true);
    await axiosApi.post('v2/accounts/password-request/', email);
    setError('');
    message.success({
      content: 'Email successfully sent!',
      duration: 3,
    });
    return true;
  } catch (error) {
    if (error.isAxiosError) {
      if (error.response.status === 500) {
        message.warning('Password reset finished with error! Try again in a while.');
        return;
      }
      handleOneLineError(error, setError);
    } else {
      throw new Error(error);
    }
    return false;
  } finally {
    setLoading(false);
  }
}

export async function submitNewPassword(code, requestBody, setError, setLoading) {
  try {
    setLoading(true);
    await axiosApi.patch(`v2/accounts/new-password/?code=${code}`, requestBody);
    setError('');
    message.success({
      content: 'Password successfully changed!',
      duration: 3,
    });
    return true;
  } catch (error) {
    if (error.isAxiosError) {
      if (
        error.response.status === 404 &&
        error.response?.data.detail === 'Not found.'
      ) {
        setError('The reset password link seems to be broken or expired');
        return;
      }
      if (error.response.status === 500) {
        message.warning('Password change finished with error! Try again in a while.');
        return;
      }
      handleOneLineError(error, setError);
    } else {
      throw new Error(error);
    }
    return false;
  } finally {
    setLoading(false);
  }
}

export async function fetchMergedCurrentUser() {
  try {
    const response = await axiosApi.get('/current_user/');
    const user = response.data;

    if (user.id) {
      const userData = await axiosApi.get(`/v2/accounts/users/${user.id}/`);
      const userInfo = userData.data;

      if (!userInfo) return;

      const isAllNotAdmin = userInfo.roles_and_perms.every((access) => access.role !== 1);

      const usersInfoLocal = {
        roles: userInfo.roles_and_perms,
        isSuperUser: userInfo.is_superuser,
      };

      await addLocalStorage('userInfo', usersInfoLocal);

      const userState = {
        id: userInfo.id,
        username: userInfo.username,
        isSuperUser: userInfo.is_superuser,
        roles: userInfo.roles_and_perms,
        isAllNotAdmin: userInfo.is_superuser ? false : isAllNotAdmin,
      };

      return { user: userState };
    }
  } catch (error) {
    console.error(error);
  }
}
